import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

import { NumberInput, SliderMinMax } from './';
import useDebounce from '@/helpers/useDebounce';

const StyledInputSliderRange = styled.div`
  ${tw`w-full`};
`;

const TopContainer = styled.div`
  ${tw`relative flex items-center w-full h-5 mb-4`};
`;

const Separator = styled.span`
  ${tw`mx-1`};
`;

const Label = styled.label`
  ${tw`text-slate-500 text-xs ml-1`};
`;

interface Props {
  minId: string;
  maxId: string;
  min: number;
  max: number;
  label: string;
  value: [number, number];
  type?: 'number' | 'decimal';
  handleValueChange: (label: string, value: number) => void;
}

const InputSliderRange: FC<Props> = ({
  min,
  minId,
  maxId,
  max,
  value,
  label,
  type = 'number',
  handleValueChange,
}) => {
  const [internalValue, setInternalValue] = useState<[number, number]>(value);

  // Debounce the value prop to prevent rapid calls
  const debouncedValue = useDebounce(internalValue, 300) as [number, number];

  // Trigger handleValueChange only when debouncedValue updates
  useEffect(() => {
    handleValueChange(minId, debouncedValue[0]);
    handleValueChange(maxId, debouncedValue[1]);
  }, [debouncedValue, minId, maxId]);

  const handleInternalChange = (id: string, newValue: number) => {
    setInternalValue((prevState) =>
      id === minId ? [newValue, prevState[1]] : [prevState[0], newValue]
    );
  };

  return (
    <StyledInputSliderRange>
      <TopContainer>
        <NumberInput
          min={min}
          max={max}
          type={type}
          value={internalValue[0]}
          handleValueChange={(newValue: number) =>
            handleInternalChange(minId, newValue)
          }
        />
        {label && <Label>{label}</Label>}
        <Separator>-</Separator>
        <NumberInput
          min={min}
          max={max}
          value={internalValue[1]}
          type={type}
          handleValueChange={(newValue: number) =>
            handleInternalChange(maxId, newValue)
          }
        />
        {label && <Label>{label}</Label>}
      </TopContainer>
      <SliderMinMax
        min={min}
        max={max}
        value={internalValue}
        step={type === 'decimal' ? 0.1 : 1}
        handleValueChange={(newValue: [number, number]) => {
          handleInternalChange(minId, newValue[0]);
          handleInternalChange(maxId, newValue[1]);
        }}
      />
    </StyledInputSliderRange>
  );
};

export default InputSliderRange;
